<template>
  <div>
    <h3 class="mb-5 text-lg font-medium text-gray-900 dark:text-white text-center">
      <span class="-mr-8">Regulatory Permissions and Advice Areas</span>
      <PrinterIcon class="h-8 w-8 float-right" aria-hidden="true" v-on:click="printPage('/regulatory-permission-print')" v-if="canPrintPage"/>
    </h3>
    <div class="group flex">
      <img class="flex-none h-14 w-14 mr-5 ml-5 mt-3 rounded-full border border-[#73C2AF] p-1" src="/images/mo-chat.png" alt="">
      <p class="border rounded-lg bg-white p-4 shadow-sm text-sm text-gray-500 border-[#73C2AF]">
        Welcome to Model Office-MO® to ensure your Model Office regulatory audit experience is tailored to your
        firm's regulatory permissions and advice areas, please ensure you tick all regulatory permissions that apply to your
        firm and regulated individuals from the list bellow. Please do not tick any that do not apply to your firm and
        regulated individuals. You can read more in the <a target="_blank" href="https://handbook.fca.org.uk/handbook/perg2" class="text-blue-400">FCA perimeter guidance manual</a>
      </p>
    </div>
    <div class="mt-4 grid grid-cols-1 gap-y-6 sm:grid-cols-3 sm:gap-x-4">
      <div
          v-for="item in items"
          :key="item.id"
          @click="update(item)"
          :class="[selectedItems.includes(item) ? 'border-[#73C2AF] ring-2 ring-[#73C2AF]' : 'border-gray-300', 'relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none']"
      >
        <span class="flex flex-1">
          <span class="flex flex-col">
            <span class="block text-sm font-medium text-gray-900">{{ item.name }}</span>
            <span class="mt-1 flex items-center text-sm text-gray-500">{{ item.description }}</span>
            <span class="mt-6 text-sm font-medium text-gray-900">{{ item.count_business_function }} business functions</span>
          </span>
        </span>
        <CheckCircleIcon :class="[!selectedItems.includes(item) ? 'invisible' : '', 'h-5 w-5 text-[#73C2AF]']" aria-hidden="true" />
        <span :class="[selectedItems.includes(item) ? 'border' : 'border-2', selectedItems.includes(item) ? 'border-[#73C2AF]' : 'border-transparent', 'pointer-events-none absolute -inset-px rounded-lg']" aria-hidden="true" />
      </div>
    </div>
  </div>

  <div class="flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8 mt-5">
    <button @click="routerBack" type="button" class="text-sm font-semibold leading-6 text-gray-900">Back</button>
    <a href="/dashboard" class="rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600">
      Done
    </a>
  </div>
</template>

<script>
import { RadioGroup, RadioGroupDescription, RadioGroupLabel, RadioGroupOption } from '@headlessui/vue'
import { CheckCircleIcon, PrinterIcon } from '@heroicons/vue/20/solid'

export default {
  name: "RegulatoryPermissionsComponent",
  computed: {
    user() {
      return this.$store.state.user;
    },
    account() {
      return this.$store.state.account;
    }
  },
  data() {
    return {
      selectedItems: [],
      items: null,
      canPrintPage: false,
    }
  },
  components: {
    RadioGroupDescription,
    RadioGroupOption,
    RadioGroupLabel,
    RadioGroup,
    CheckCircleIcon,
    PrinterIcon,
  },
  methods: {
    getItems() {
      let path = `/regulatory-permission`;

      if (this.account.id !== this.user.account.id) {
        path = `/member-firm/regulatory-permission/${this.account.id}`;
      }

      axios.get(path).then((res) => {
        this.items = res.data.data;
        this.selectedItems = this.items.filter(item => item.selected === true);
        this.canPrintPage = res.data.print_page;
      }).catch((error) => {
        console.log('error', error)
      })
    },
    update(item) {
      let pathAttach = `/regulatory-permission/attach`;
      let pathDetach = `/regulatory-permission/detach`;

      if (this.account.id !== this.user.account.id) {
        pathAttach = `/member-firm/regulatory-permission/${this.account.id}/attach`;
        pathDetach = `/member-firm/regulatory-permission/${this.account.id}/detach`;
      }

      const isSelected = this.selectedItems.includes(item);

      if (isSelected) {
        const index = this.selectedItems.indexOf(item);
        if (index > -1) {
          this.selectedItems.splice(index, 1);
        }

        axios.post(pathDetach, item)
            .then((res) => {
              this.$notify({ type: "success", title: "Successfully", text: res.data.message });
            })
            .catch((error) => {
              console.log('error', error);
              this.$notify({ title: "Unsuccessfully", text: error.message });
              this.getItems();
            });
      } else {
        this.selectedItems.push(item);

        axios.post(pathAttach, item)
            .then((res) => {
              this.$notify({ type: "success", title: "Successfully", text: res.data.message });
            })
            .catch((error) => {
              console.log('error', error);
              this.$notify({ title: "Unsuccessfully", text: error.message });
              this.getItems();
            });
      }
    },
    saveRegulatoryPermissionsIsShown() {
      axios.post(`/regulatory-permission/is-shown`)
          .then((res) => {
            this.$notify({ type: "success", title: "Successfully", text: res.data.message });
          })
          .catch((error) => {
            console.log('error', error);
            this.$notify({ title: "Unsuccessfully", text: error.message });
          });
    },
    routerBack() {
      this.$router.back();
    },
    printPage(src) {
      const iframe = document.createElement('iframe');
      iframe.setAttribute('src', src);
      iframe.setAttribute('height', '0');
      iframe.setAttribute('name', 'for_print');
      iframe.setAttribute('id', 'for_print');
      iframe.style.position = 'absolute';
      document.body.appendChild(iframe);

      iframe.addEventListener('load', function() {
        const printWindow = iframe.contentWindow;
        printWindow.focus();
        printWindow.print();
        setTimeout(function() {
          iframe.remove();
        }, 100);
      });
    }
  },
  created() {
    this.getItems();
    if (!this.user.account.regulatory_permissions_is_shown && (this.account.id === this.user.account.id)) {
      this.saveRegulatoryPermissionsIsShown();
    }
  },
}
</script>
